import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChartColumn, ScrollText, User } from 'lucide-react';
import { Badge, Button, Card, Notice, SectionTitle, SelectField, Stat, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { TabsShell } from '../../components/TabsShell';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { AccountSettings } from '../common/AccountSettings';

interface TenantRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  createdAt: string;
  plan: { code: string; name: string; status: string } | null;
  domains: Array<{ id: string; domain: string; verified: boolean }>;
  counts: { drivers: number; vehicles: number; customers: number; rides: number };
}
interface Plan {
  id: string;
  code: string;
  name: string;
  maxDrivers: number;
  maxVehicles: number;
  priceMonthly: string;
}

const emptyTenant = { name: '', slug: '', ownerEmail: '', ownerFirstName: '', ownerLastName: '', planCode: 'SOLO', ownerIsDriver: true };

function TenantsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => api<TenantRow[]>('/tenants') });
  const plans = useQuery({ queryKey: ['admin', 'plans'], queryFn: () => api<Plan[]>('/plans') });
  const [f, setF] = useState(emptyTenant);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api<{ slug: string; temporaryPassword: string | null }>('/tenants', { body: f });
      setResult(`Negocio "${r.slug}" creado.${r.temporaryPassword ? ` Contraseña temporal del propietario: ${r.temporaryPassword}` : ' El propietario ya tenía cuenta.'}`);
      setF(emptyTenant);
      await qc.invalidateQueries({ queryKey: ['admin'] });
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  const act = (p: Promise<unknown>) => p.then(() => qc.invalidateQueries({ queryKey: ['admin'] })).catch((e) => toast(errorMessage(e), 'danger'));
  const setStatus = (id: string, status: string) => act(api(`/tenants/${id}/status`, { method: 'PATCH', body: { status } }));
  const setPlan = (id: string, planCode: string) => act(api(`/tenants/${id}/plan`, { method: 'PATCH', body: { planCode } }));

  return (
    <Page title="Negocios" onRefresh={() => tenants.refetch()}>
      <div className="flex flex-col gap-4">
        {tenants.data?.map((t) => (
          <Card key={t.id} className="p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold">
                {t.name} <span className="text-xs text-[var(--text-muted)]">/{t.slug}</span>
              </p>
              <Badge tone={t.status === 'ACTIVE' || t.status === 'TRIAL' ? 'success' : 'danger'}>{t.status}</Badge>
            </div>
            <p className="text-sm text-[var(--text-muted)]">
              {t.counts.drivers} conductores · {t.counts.vehicles} vehículos · {t.counts.customers} clientes · {t.counts.rides} viajes
            </p>
            {t.domains.length > 0 && <p className="text-xs">{t.domains.map((d) => `${d.domain}${d.verified ? ' ✓' : ''}`).join(', ')}</p>}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <SelectField label="Plan" value={t.plan?.code ?? ''} onChange={(e) => setPlan(t.id, e.target.value)}>
                  {!t.plan && <option value="">—</option>}
                  {plans.data?.map((p) => (
                    <option key={p.code} value={p.code}>{p.name}</option>
                  ))}
                </SelectField>
              </div>
              {t.status === 'SUSPENDED' ? (
                <Button size="sm" variant="secondary" onClick={() => setStatus(t.id, 'ACTIVE')}>Reactivar</Button>
              ) : (
                <Button size="sm" variant="danger" onClick={() => setStatus(t.id, 'SUSPENDED')}>Suspender</Button>
              )}
            </div>
          </Card>
        ))}
        <Card className="p-5 flex flex-col gap-3">
          <SectionTitle title="Nuevo negocio" subtitle="Se crean los servicios por defecto y la cuenta del propietario." />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Nombre" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <TextField label="Identificador" hint="minúsculas-y-guiones" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} />
            <TextField label="Nombre del propietario" value={f.ownerFirstName} onChange={(e) => setF({ ...f, ownerFirstName: e.target.value })} />
            <TextField label="Apellido" value={f.ownerLastName} onChange={(e) => setF({ ...f, ownerLastName: e.target.value })} />
          </div>
          <TextField label="Correo del propietario" type="email" value={f.ownerEmail} onChange={(e) => setF({ ...f, ownerEmail: e.target.value })} />
          <SelectField label="Plan" value={f.planCode} onChange={(e) => setF({ ...f, planCode: e.target.value })}>
            {plans.data?.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} · {p.maxDrivers} conductores · {p.maxVehicles} vehículos
              </option>
            ))}
          </SelectField>
          <label className="tap flex items-center gap-3 text-sm">
            <input type="checkbox" className="size-5 accent-[#E5252A]" checked={f.ownerIsDriver} onChange={(e) => setF({ ...f, ownerIsDriver: e.target.checked })} /> El propietario también conduce
          </label>
          <Button loading={busy} onClick={create}>Crear negocio</Button>
          {result && <Notice>{result}</Notice>}
        </Card>
      </div>
    </Page>
  );
}

interface Stats {
  tenants: Record<string, number>;
  users: number;
  last30Days: { rides: Record<string, number>; requestsByChannel: Record<string, number> };
}

function StatsPage() {
  const q = useQuery({ queryKey: ['admin', 'stats'], queryFn: () => api<Stats>('/admin/stats') });
  const d = q.data;
  return (
    <Page title="Estadísticas" onRefresh={() => q.refetch()}>
      {d && (
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Negocios activos" value={(d.tenants.ACTIVE ?? 0) + (d.tenants.TRIAL ?? 0)} />
          <Stat label="Negocios suspendidos" value={d.tenants.SUSPENDED ?? 0} />
          <Stat label="Usuarios" value={d.users} />
          <Stat label="Viajes completados (30 d)" value={d.last30Days.rides.COMPLETED ?? 0} />
          <Stat label="Cancelados (30 d)" value={d.last30Days.rides.CANCELLED ?? 0} />
          <Stat label="Solicitudes web/app (30 d)" value={(d.last30Days.requestsByChannel.WEB ?? 0) + (d.last30Days.requestsByChannel.APP ?? 0)} />
          <Stat label="Solicitudes WhatsApp (30 d)" value={d.last30Days.requestsByChannel.WHATSAPP ?? 0} />
        </div>
      )}
    </Page>
  );
}

interface AuditRow {
  id: string;
  action: string;
  tenantId: string | null;
  actorRole: string | null;
  entityType: string | null;
  ip: string | null;
  createdAt: string;
}

function AuditPage() {
  const [action, setAction] = useState('');
  const q = useQuery({ queryKey: ['admin', 'audit', action], queryFn: () => api<AuditRow[]>('/admin/audit', { query: { action: action || undefined } }) });
  return (
    <Page title="Auditoría" onRefresh={() => q.refetch()}>
      <div className="flex flex-col gap-3">
        <TextField label="Filtrar por acción" placeholder="security., auth., payment., tenant." value={action} onChange={(e) => setAction(e.target.value)} />
        <Card className="divide-y divide-[var(--border)] text-sm">
          {q.data?.map((a) => (
            <div key={a.id} className="px-4 py-2.5">
              <p className="font-mono text-xs">{a.action}</p>
              <p className="text-xs text-[var(--text-muted)]">
                {fmtDateTime(a.createdAt)} · {a.actorRole ?? 'sin rol'}
                {a.entityType ? ` · ${a.entityType}` : ''}
                {a.ip ? ` · ${a.ip}` : ''}
              </p>
            </div>
          ))}
          {q.data?.length === 0 && <p className="p-4 text-[var(--text-muted)]">Sin eventos.</p>}
        </Card>
      </div>
    </Page>
  );
}

const AdminAccount = () => (
  <Page title="Cuenta">
    <AccountSettings />
  </Page>
);

/** Panel de plataforma (SUPER_ADMIN). */
export function AdminShell() {
  return (
    <TabsShell
      base="/admin"
      home="/admin/tenants"
      tabs={[
        { tab: 'tenants', href: '/admin/tenants', label: 'Negocios', icon: Building2 },
        { tab: 'stats', href: '/admin/stats', label: 'Estadísticas', icon: ChartColumn },
        { tab: 'audit', href: '/admin/audit', label: 'Auditoría', icon: ScrollText },
        { tab: 'account', href: '/admin/account', label: 'Cuenta', icon: User },
      ]}
      routes={[
        { path: '/admin/tenants', component: TenantsPage },
        { path: '/admin/stats', component: StatsPage },
        { path: '/admin/audit', component: AuditPage },
        { path: '/admin/account', component: AdminAccount },
      ]}
    />
  );
}
